import { useCallback, useMemo, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { usePreviewAvatarContext } from '@avatar/context/PreviewAvatarContext';
import isBrowser from '../../../util/isBrowser';
import fetch from '../../../lib/fetch';
import useSelectedPreviewActor from '../../avatar/hooks/useSelectedPreviewActor';
import { useEventsContext } from '../../../context/events/EventsContext';

type Preview = {
  src: string;
  text: string;
  loading: boolean;
  error: string | null;
};

const createObjectURL = (blob: Blob) => {
  if (isBrowser && typeof URL.createObjectURL === 'function') {
    return URL.createObjectURL(blob);
  }
  return undefined;
};

/**
 * @description promise wrapped method which fetches a preview of an avatar using
 * the fetch api
 * @param {PlaybackProps} PlaybackProps
 */
export const getPreview = async ({
  avatarId,
  avatarVariantId,
  version,
  text,
  isRespelling,
  originalText,
  isAudition = false,
}: {
  text: string;
  avatarId?: number;
  avatarVariantId?: number;
  version?: string;
  isRespelling?: boolean;
  originalText?: string;
  isAudition?: boolean;
}) => {
  const response = await fetch(
    `/api/text_to_speech/preview${!isAudition ? '' : '/audition'}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify({
        text,
        avatarId,
        avatarVariantId,
        voiceId: avatarId,
        voiceVariantId: avatarVariantId,
        version,
        isRespelling,
        originalText,
      }),
    }
  );
  if (response.ok) {
    const responseBlob = await response.blob();
    const objectUrl = createObjectURL(responseBlob);
    // resolve as promise to guarantee value is string on success
    return objectUrl
      ? Promise.resolve(objectUrl)
      : Promise.reject(new Error('unable to create preview'));
  }
  const resp = await response.json();
  return Promise.reject(new Error(resp.message || resp.error));
};

const EMPTY_INPUT_STATE = { src: '', text: '', loading: false, error: null };

export const useInputChange = (initialText?: string, onChange?: () => void) => {
  const initialState = EMPTY_INPUT_STATE;
  if (initialText && initialText !== '') {
    initialState.text = initialText;
  } else {
    initialState.text = '';
  }
  const [{ src, text, loading, error: errorMsg }, updateInput] =
    useState<Preview>(initialState);
  const router = useRouter();
  const path = router?.route!;
  const [eventTracking] = useEventsContext();
  const { selectedPreviewActor: legacyPreviewActor } =
    useSelectedPreviewActor();
  // undefined if legacy project (i.e. component not wrapped by PreviewAvatarProvider)
  const scriptStudioPreviewAvatar = usePreviewAvatarContext();

  const previewAvatar = scriptStudioPreviewAvatar ?? legacyPreviewActor;

  const onTextChange = useCallback(
    (currentText: string) => {
      updateInput(state => ({
        ...state,
        src: '',
        loading: false,
        text: currentText,
        error: null,
      }));
      // call optional on change method
      if (onChange) onChange();
    },
    [updateInput, onChange]
  );

  const reset = useCallback(() => {
    updateInput(EMPTY_INPUT_STATE);
  }, [updateInput]);

  const handleInitialLoad = useCallback(
    async (
      isRespelling: boolean = false,
      originalText: string = '',
      location: string = ''
    ) => {
      updateInput(state => ({ ...state, loading: true, error: null }));

      try {
        const payload = {
          ...previewAvatar,
          text,
          isRespelling,
          originalText,
        };
        const objectUrl = await getPreview(payload);
        eventTracking.trackPhoneticLibraryPreviewRender({
          ...previewAvatar,
          path,
          location,
        });
        updateInput(state => ({ ...state, src: objectUrl, loading: false }));
      } catch (err) {
        if (err instanceof Error) {
          const message = err?.message;
          updateInput(state => ({
            ...state,
            src: '',
            loading: false,
            error: message,
          }));
        }
      }
    },
    [eventTracking, previewAvatar, text, path]
  );

  useEffect(() => {
    updateInput(state => ({
      ...state,
      src: '',
      error: null,
    }));
  }, [previewAvatar]);

  return useMemo(() => {
    return {
      errorMsg,
      src,
      loading,
      text,
      onTextChange,
      handleInitialLoad,
      reset,
    };
  }, [errorMsg, handleInitialLoad, text, onTextChange, src, loading, reset]);
};

export default useInputChange;
